import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useMe } from '@/data/auth';
import { api } from '@/lib/api-client';
import type { User } from '@/stores/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

const profileSchema = z.object({
  firstname: z.string().min(1),
  lastname:  z.string().min(1),
});
type ProfileForm = z.infer<typeof profileSchema>;

const passwordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword:     z.string().min(8),
  confirm:         z.string(),
}).refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'Stimmt nicht überein.' });
type PasswordForm = z.infer<typeof passwordSchema>;

export function AccountPage() {
  useDocumentTitle('Konto');
  const { data: me, isLoading, refetch } = useMe();

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    if (me) reset({ firstname: me.firstname, lastname: me.lastname });
  }, [me, reset]);

  const onProfileSubmit = async (values: ProfileForm) => {
    try {
      await api<User>('/api/v1/account', { method: 'PATCH', body: values });
      await refetch();
      toast.success('Profil aktualisiert.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  const pwdForm = useForm<PasswordForm>({ resolver: zodResolver(passwordSchema) });

  const onPwdSubmit = async (values: PasswordForm) => {
    try {
      await api('/api/v1/account/change-password', {
        method: 'POST',
        body: { currentPassword: values.currentPassword, newPassword: values.newPassword },
      });
      pwdForm.reset();
      toast.success('Passwort geändert.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  if (isLoading || !me) {
    return <div className="grid h-full place-items-center p-8"><Spinner className="h-8 w-8" /></div>;
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 md:p-8">
      <h1 className="text-2xl font-semibold">Konto</h1>

      <Card>
        <CardHeader><CardTitle>Profil</CardTitle></CardHeader>
        <CardContent>
          <form className="grid gap-4 md:max-w-lg" onSubmit={handleSubmit(onProfileSubmit)}>
            <div className="space-y-1.5">
              <Label htmlFor="email">E-Mail</Label>
              <Input id="email" value={me.email} disabled />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="firstname">Vorname</Label>
                <Input id="firstname" {...register('firstname')} />
                {errors.firstname && <p className="text-xs text-[var(--destructive)]">{errors.firstname.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastname">Nachname</Label>
                <Input id="lastname" {...register('lastname')} />
                {errors.lastname && <p className="text-xs text-[var(--destructive)]">{errors.lastname.message}</p>}
              </div>
            </div>
            <div>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Spinner /> : 'Speichern'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Passwort ändern</CardTitle></CardHeader>
        <CardContent>
          <form className="grid gap-4 md:max-w-lg" onSubmit={pwdForm.handleSubmit(onPwdSubmit)}>
            <div className="space-y-1.5">
              <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
              <Input id="currentPassword" type="password" autoComplete="current-password" {...pwdForm.register('currentPassword')} />
              {pwdForm.formState.errors.currentPassword && (
                <p className="text-xs text-[var(--destructive)]">{pwdForm.formState.errors.currentPassword.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="newPassword">Neues Passwort</Label>
              <Input id="newPassword" type="password" autoComplete="new-password" {...pwdForm.register('newPassword')} />
              {pwdForm.formState.errors.newPassword && (
                <p className="text-xs text-[var(--destructive)]">{pwdForm.formState.errors.newPassword.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Wiederholen</Label>
              <Input id="confirm" type="password" autoComplete="new-password" {...pwdForm.register('confirm')} />
              {pwdForm.formState.errors.confirm && (
                <p className="text-xs text-[var(--destructive)]">{pwdForm.formState.errors.confirm.message}</p>
              )}
            </div>
            <div>
              <Button type="submit" disabled={pwdForm.formState.isSubmitting}>
                {pwdForm.formState.isSubmitting ? <Spinner /> : 'Passwort speichern'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
